// Small shared display helpers for the CRM feature.
import StatusPill from "../../../shared/StatusPill";
import { Tone, TONE_SOFT } from "../../../shared/tones";
import type { GatesVerdict, GatesSummary } from "../api/CrmApi";

// Shown by CrmPage/CrmAnalyticsPage when the seeded sled_rfp pipeline isn't
// found (e.g. seeders.py hasn't run yet on a fresh DB) — otherwise the page
// would silently render an empty board with no explanation.
export function PipelineNotSetUp() {
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-16 text-center">
      <p className="text-sm font-medium text-foreground">SLED CRM pipeline not found</p>
      <p className="text-xs text-muted-foreground max-w-sm">
        The seeded "sled_rfp" pipeline is missing. If this is a fresh environment, make sure the
        backend's seeders have run; otherwise contact an admin.
      </p>
    </div>
  );
}

// A manually-entered URL (website, LinkedIn, ...) commonly gets typed/pasted
// without a scheme (e.g. "linkedin.com/company/x") — used verbatim as an
// href, the browser resolves it as a relative path off the current admin
// page instead of opening it. Only prepends when no scheme is present.
export function ensureHttpUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

// One semantic tone per pipeline stage — shared by the StageBadge pill, the
// Kanban column dots and the dashboard's Deadline Radar, so a stage reads the
// same color everywhere (and in both themes).
export const STAGE_TONES: Record<string, Tone> = {
  received: "neutral",
  fit_review: "magenta",
  qualified: "info",
  on_hold: "orange",
  role_review: "warning",
  assets_built: "brand",
  deployed: "teal",
  outreach_active: "warning",
  closed_won: "success",
  closed_lost: "danger",
  disqualified: "neutral",
  not_fit: "neutral",
};

export function stageTone(stageKey: string): Tone {
  return STAGE_TONES[stageKey] ?? "neutral";
}

export function StageBadge({ stageKey, stageName }: { stageKey: string; stageName: string }) {
  return (
    <StatusPill tone={stageTone(stageKey)} dot>
      {stageName}
    </StatusPill>
  );
}

// The backend stores two kinds of naive (timezone-less) datetimes:
// - deadlines are agency WALL-CLOCK times ("2:00 PM" as printed on the RFP)
//   and must render exactly as stored — new Date() on a timezone-less string
//   parses as local, which is exactly that behavior;
// - *_at timestamps (created/updated/page_built/activities...) are UTC and
//   must be shifted to the viewer's local time, so we append "Z" first.
const HAS_TZ = /(Z|[+-]\d{2}:?\d{2})$/;

function parseDb(value: string, utc: boolean): Date | null {
  const s = utc && !HAS_TZ.test(value) ? `${value}Z` : value;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Date-only display. Pass utc=true for *_at timestamps; default is wall-clock (deadlines). */
export function formatDate(value: string | null | undefined, utc = false): string {
  if (!value) return "—";
  const d = parseDb(value, utc);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Date+time display for *_at timestamps — always treats naive values as UTC. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = parseDb(value, true);
  if (!d) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Days from today until the given deadline (wall-clock; negative = past). */
export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  const d = parseDb(value, false);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

/** Colored day-count label for a deadline: red when past/urgent, amber soon, slate otherwise. */
export function DeadlineChip({ value }: { value: string | null | undefined }) {
  const days = daysUntil(value);
  if (days === null) return <span className="text-muted-foreground">—</span>;
  let cls = "text-muted-foreground";
  let label = `${days}d left`;
  if (days < 0) { cls = "text-destructive"; label = `${Math.abs(days)}d ago`; }
  else if (days === 0) { cls = "text-destructive"; label = "today"; }
  else if (days <= 5) { cls = "text-warning-soft-foreground"; }
  return (
    <span className="whitespace-nowrap">
      {formatDate(value)}{" "}
      <span className={`text-[11px] font-medium ${cls}`}>({label})</span>
    </span>
  );
}

/** Small ✓/– indicator for the artifact checklist. */
export function ArtifactDot({ done, label, name }: { done: boolean; label: string; name?: string }) {
  return (
    <span
      title={`${name ?? label}: ${done ? "done" : "pending"}`}
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md border whitespace-nowrap ${
        done ? TONE_SOFT.success : "text-muted-foreground border-border bg-surface-muted/50"
      }`}
    >
      {done ? "✓" : "·"} {label}
    </span>
  );
}

// ── /rfp-gates verdicts ──────────────────────────────────────────────────────

export const GATES_VERDICT_LABELS: Record<GatesVerdict, string> = {
  PROCEED: "Proceed",
  PROCEED_WITH_CONDITIONS: "Conditions",
  HOLD_VERIFY: "Hold / Verify",
  STOP: "Stop",
};

export const GATES_VERDICT_TONES: Record<GatesVerdict, Tone> = {
  PROCEED: "success",
  PROCEED_WITH_CONDITIONS: "info",
  HOLD_VERIFY: "orange",
  STOP: "danger",
};

/** Verdict pill; a muted "Not checked" when /rfp-gates never ran for this RFP. */
export function GatesVerdictPill({ verdict, size = "md" }: { verdict: GatesVerdict | null | undefined; size?: "sm" | "md" }) {
  if (!verdict) {
    return <span className="text-[11px] text-muted-foreground" title="/rfp-gates has not been run for this RFP">Not checked</span>;
  }
  return (
    <StatusPill tone={GATES_VERDICT_TONES[verdict]} size={size} dot>
      {GATES_VERDICT_LABELS[verdict]}
    </StatusPill>
  );
}

/** One short line under the verdict: what the gates found, in the list/board views. */
export function gatesDetailLine(verdict: GatesVerdict | null | undefined, summary: GatesSummary | null | undefined): string | null {
  if (!verdict || !summary) return null;
  const parts: string[] = [];
  if (verdict === "STOP" && summary.stop_reasons > 0) {
    parts.push(`${summary.stop_reasons} stop reason${summary.stop_reasons === 1 ? "" : "s"}`);
  }
  if (summary.conditions > 0) parts.push(`${summary.conditions} condition${summary.conditions === 1 ? "" : "s"}`);
  if (summary.to_verify > 0) {
    const kind = summary.eligibility_holds === 0 ? " (money terms only)" : "";
    parts.push(`${summary.to_verify} to verify${kind}`);
  }
  if (summary.time_critical) parts.push("urgent");
  if (summary.deadline_passed) parts.push("closed");
  return parts.length ? parts.join(" · ") : null;
}

export const ACTIVITY_LABELS: Record<string, string> = {
  created: "Created",
  cli_upsert: "Synced from CLI",
  stage_change: "Stage changed",
  note: "Note",
  checked: "RFP checked",
  page_built: "Decoded page built",
  email_built: "Outreach email built",
  subject_finalized: "Subject line finalized",
  primes_extracted: "US Primes extracted",
  deployed: "Page deployed",
  outreach_drafted: "Outreach campaign drafted",
  outreach_started: "Outreach started",
  folder_archived: "Folder archived",
  fit_checked: "Software fit checked",
  gates_checked: "Qualification gates checked",
  outreach_reply: "Reply received",
  outreach_open: "Email opened",
  outreach_click: "Link clicked",
  outreach_bounce: "Email bounced",
  outreach_unsubscribe: "Unsubscribed",
};
